import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ImageUploader } from "./ImageUploader";

interface EditShopItemDialogProps {
  item: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  categories: any[];
}

export function EditShopItemDialog({ item, open, onOpenChange, onSuccess, categories }: EditShopItemDialogProps) {
  const [formData, setFormData] = useState({
    name: item.name,
    description: item.description || "",
    category: item.category,
    images: item.images || [],
    price: item.price || "",
    contact_method: item.contact_method || "",
    condition: item.condition || "",
    is_featured: item.is_featured || false,
    ribbon: item.ribbon || "",
    is_clothing: !!item.garment_size,
    garment_size: item.garment_size || ""
  });

  const conditionOptions = [
    { value: "new", label: "New" },
    { value: "like_new", label: "Like New" },
    { value: "good", label: "Good" },
    { value: "fair", label: "Fair" }
  ];

  const ribbonOptions = [
    { value: "", label: "None" },
    { value: "sale", label: "Sale" },
    { value: "new", label: "New" }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updateData: any = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      images: formData.images,
      contact_method: formData.contact_method,
      condition: formData.condition || null,
      is_featured: formData.is_featured,
      ribbon: formData.ribbon || null,
      garment_size: formData.is_clothing && formData.garment_size ? formData.garment_size.trim() : null
    };

    if (formData.price) {
      updateData.price = parseFloat(formData.price as any);
    }

    const { error } = await supabase
      .from('shop_items')
      .update(updateData)
      .eq('id', item.id);

    if (error) {
      toast({
        title: "Error updating item",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Success",
        description: "Shop item updated!"
      });
      onOpenChange(false);
      onSuccess();
    }
  };

  const handleImagesChange = (urls: string[]) => {
    setFormData({ ...formData, images: urls });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Shop Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              maxLength={100}
            />
          </div>
          <div>
            <Label>Price (£)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="Optional"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              maxLength={1000}
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contact Method</Label>
            <Input
              value={formData.contact_method}
              onChange={(e) => setFormData({ ...formData, contact_method: e.target.value })}
              placeholder="e.g., Email or Phone"
            />
          </div>
          <div>
            <Label>Condition *</Label>
            <Select
              value={formData.condition}
              onValueChange={(value) => setFormData({ ...formData, condition: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select condition" />
              </SelectTrigger>
              <SelectContent>
                {conditionOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="clothing-toggle-edit" className="text-sm font-medium cursor-pointer">Clothing Item</Label>
              <p className="text-xs text-muted-foreground">Enable to add garment condition and size details</p>
            </div>
            <Switch
              id="clothing-toggle-edit"
              checked={formData.is_clothing}
              onCheckedChange={(checked) => setFormData({
                ...formData,
                is_clothing: checked,
                ...(!checked ? { garment_size: "" } : {})
              })}
            />
          </div>
          {formData.is_clothing && (
            <div className="space-y-4 rounded-lg border border-dashed p-4">
              <div className="space-y-2">
                <Label>Garment Condition</Label>
                <Select
                  value={formData.condition}
                  onValueChange={(value) => setFormData({ ...formData, condition: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="garment-size-edit">Size</Label>
                <Input
                  id="garment-size-edit"
                  value={formData.garment_size}
                  onChange={(e) => setFormData({ ...formData, garment_size: e.target.value })}
                  placeholder="e.g., Age 7-8, M, 32in"
                  maxLength={50}
                />
                <p className="text-xs text-muted-foreground">Enter the size as shown on the garment label</p>
              </div>
            </div>
          )}
          <div>
            <Label>Ribbon Label</Label>
            <Select
              value={formData.ribbon}
              onValueChange={(value) => setFormData({ ...formData, ribbon: value === "none" ? "" : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                {ribbonOptions.map(opt => (
                  <SelectItem key={opt.value || "none"} value={opt.value || "none"}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Optional ribbon displayed on the item card</p>
          </div>
          <div>
            <Label>Images (up to 3)</Label>
            <ImageUploader
              ptaId={item.pta_id}
              itemId={item.id}
              images={formData.images}
              onChange={handleImagesChange}
              maxImages={3}
            />
          </div>
          <Card className={`border-2 ${formData.is_featured ? 'border-primary bg-primary/5' : 'border-dashed'}`}>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="featured-edit"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked as boolean })}
                />
                <div className="space-y-1">
                  <Label htmlFor="featured-edit" className="text-sm font-medium cursor-pointer">
                    Featured Listing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Featured listings appear first at the top of the marketplace with a special banner.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1">
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
